<script setup lang="ts">
import {onMounted, ref} from "vue";
import {useRouter} from "vue-router";
import {useCommunicationStore} from "@/communication/stores/communication.store";
import {storeToRefs} from "pinia";
import {useCreditStore} from "@/stores/credit.store";
import {ArrowRight} from "@lucide/vue";
import CommunicationToolbar from "@/communication/components/toolbar/CommunicationToolbar.vue";
import AudienceCard from "@/communication/components/audience/AudienceCard.vue";
import {MassCertificationCustomer} from "@/core/interfaces/MassCertification";
import BaseCard from "@/core/components/base/BaseCard.vue";
import BaseCardBody from "@/core/components/base/BaseCardBody.vue";
import {pluralize} from "@/core/filters/Pluralize";
import BaseButton from "@/core/components/base/BaseButton.vue";
import BaseContainer from "@/core/components/base/BaseContainer.vue";

const communicationStore = useCommunicationStore();
const creditStore = useCreditStore();

const {customers, mode: storeMode} = storeToRefs(communicationStore);
const {credit} = storeToRefs(creditStore);

const loading = ref<boolean>(false);
const router = useRouter();

const selected = ref<MassCertificationCustomer[]>([]);

const mode = ref<"sms" | "notification">("sms");

const updateMode = (isNotification: boolean) => {
    mode.value = isNotification ? 'notification' : 'sms';
};

onMounted(() => {
    communicationStore.$reset();
    creditStore.refresh();
});

/**
 * Set the values in the store and go to the message selection step.
 */
const confirm = () => {
    customers.value = selected.value;
    storeMode.value = mode.value;

    router.push({
        name: 'patient-administration.communication.message.' + mode.value,
    });
};
</script>

<template>
  <BaseContainer class="p-4 flex flex-col gap-4">
    <div class="flex flex-row justify-between items-center">
      <h1>Communiquez auprès de vos patients</h1>
    </div>
    <CommunicationToolbar
      v-model:mode="mode"
      :loading="loading"
      :credit-balance="credit?.balance ?? 0"
      @on-notification-change="updateMode"
    />
    <AudienceCard
      v-model:selected="selected"
      :mode="mode"
      @loading="loading = $event"
    />

    <BaseCard v-if="selected.length">
      <BaseCardBody class="flex justify-between items-center">
        <div>
          <span class="text-lg">
            <span
              class="text-xl font-medium" :class="{
                'text-primary': selected.length > 0,
                'text-muted-foreground': !selected.length
              }"
            >
              {{ selected.length }}
            </span>
            {{ pluralize('patient', selected.length) }} {{ pluralize('sélectionné', selected.length) }}
          </span>
          <p class="text-sm text-muted-foreground">
            Coût en crédits calculé à l'étape de sélection du message
          </p>
        </div>
        <BaseButton primary :disabled="!selected.length" @click="confirm()">
          Continuer <ArrowRight class="size-4 ml-1" />
        </BaseButton>
      </BaseCardBody>
    </BaseCard>
  </BaseContainer>
</template>
